import { FormProvider, useForm } from "react-hook-form";
import { useCallback, useState } from "react";
import DirectoryPickerInput from "@/components/shared/form/fields/DirectoryPickerInput.tsx";
import FormActions from "@/components/shared/form/fields/FormActions.tsx";
import { useMutation } from "react-query";
import { fetchApi } from "@/helpers/reactQueryApi.ts";
import { join, map } from "lodash";
import { useParams } from "@/helpers/tixxt-router.tsx";
import type { PermissionPickableWithDisabled } from "@/components/shared/DirectoryPicker/types.ts";

type SelectionEntry = {
  id: string;
  // Members come back from /directory/members as type "member";
  // assignable_invites returns type "external_invite". Group mount uses the
  // legacy picker which only ever yields "member" — type is optional there.
  type?: "member" | "external_invite";
};
type FormShape = {
  // Network mount: ONE unified picker; entries discriminate via `type`.
  role_assignment_selection?: SelectionEntry[];
  // Group mount: unchanged single members-only picker.
  memberships?: SelectionEntry[];
};

export default function RoleAssignmentsForm() {
  const { groupSlug } = useParams();
  const methods = useForm<FormShape>();
  const { handleSubmit } = methods;
  const [submitError, setSubmitError] = useState<string | null>(null);

  const endpoint = groupSlug
    ? `/groups/${groupSlug}/administration/role_assignments`
    : "/administration/role_assignments";

  // REV-02: on the Network mount, the form exposes a SINGLE DirectoryPicker
  // whose option list is the union of `/directory/members` (the picker's
  // primary endpoint) and `/administration/role_assignments/assignable_invites`
  // (supplied via the DirectoryPicker `extraOptions` extension point).
  //
  // DirectoryPicker.groupOptions() in optionLoading.ts groups options by
  // entry-type automatically: `member` entries land under
  // js.permission_picker.members.group_label ("Nutzer") and `external_invite`
  // entries under js.permission_picker.external_invites.group_label
  // ("Eingeladene Nutzer"). No new i18n keys are required (Plan 05-06
  // i18n_decision scenario 1).
  //
  // The Group mount stays untouched — there are no assignable invites in
  // the group context, so a plain members-only picker is the right shape.
  const showUnifiedPicker = !groupSlug;

  const fetchAssignableInvites = useCallback(
    async (q: string): Promise<PermissionPickableWithDisabled[]> => {
      const url =
        "/administration/role_assignments/assignable_invites" +
        (q ? `?q=${encodeURIComponent(q)}` : "");
      const res = await fetchApi(url);
      return (res?.entries ?? []) as PermissionPickableWithDisabled[];
    },
    [],
  );

  // 403 here is LimitExhausted; everything else falls back to a generic error.
  const { mutate: assignRoles } = useMutation<
    unknown,
    { status?: number; message?: string },
    FormData
  >((body) => fetchApi(endpoint, { method: "POST", body }), {
    onSuccess: () => location.reload(),
    onError: (err) => {
      const msg =
        err?.status === 403
          ? I18n.t(
              "js.administration.role_assignments.index.quota_exhausted_at_submit",
            )
          : I18n.t("js.generic_error");
      setSubmitError(msg);
    },
  });

  function submit(values: FormShape) {
    // Network mount: partition the unified picker's selection by entry.type
    // into the two CSV halves the controller expects. The server-facing
    // payload shape is unchanged from before consolidation.
    //
    // Group mount: only the legacy memberships picker is rendered; its
    // entries are always members, so we feed them straight into the
    // memberships CSV and emit an empty external_invites CSV (the existing
    // unconditional-emit pattern).
    const selection = values.role_assignment_selection ?? [];
    const memberIds = showUnifiedPicker
      ? map(
          selection.filter((e) => e.type !== "external_invite"),
          "id",
        )
      : map(values.memberships ?? [], "id");
    const inviteIds = showUnifiedPicker
      ? map(
          selection.filter((e) => e.type === "external_invite"),
          "id",
        )
      : [];

    const membershipCsv = join(memberIds, ",");
    const inviteCsv = join(inviteIds, ",");

    const body = new FormData();
    // Both CSVs are sent unconditionally — the server ignores empty halves
    // and applies the populated one.
    body.append("role_assignment[memberships][csv]", membershipCsv);
    body.append("role_assignment[external_invites][csv]", inviteCsv);

    setSubmitError(null);
    assignRoles(body);
  }

  return (
    <FormProvider {...methods}>
      <form className="flex flex-col gap-2.5" onSubmit={handleSubmit(submit)}>
        <div>
          <h3 className="text-base font-semibold mb-2">
            {I18n.t(
              "js.administration.role_assignments.index.add_registered_admins",
            )}
          </h3>
          {showUnifiedPicker ? (
            <DirectoryPickerInput
              name="role_assignment_selection"
              endpoint="/directory/members"
              extraOptions={fetchAssignableInvites}
              multi
            />
          ) : (
            <DirectoryPickerInput
              name="memberships"
              endpoint="/directory/members"
              multi
            />
          )}
        </div>

        {submitError && (
          <div role="alert" className="text-red-700 mt-2">
            {submitError}
          </div>
        )}

        <FormActions
          saveText={I18n.t("administration.role_assignments.index.save")}
          showCancel={false}
        />
      </form>
    </FormProvider>
  );
}
